import { inject, Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, Params, ResolveFn } from '@angular/router';
import { from, map, Observable, of, switchMap } from 'rxjs';
import { Key, parseArray } from 'src/app/common/utils/helpers';
import { SearchResultsFilterablesService } from 'utils/search-results-filterable';

@Injectable({
  providedIn: 'root',
})
export class SearchEngineResolver {
  constructor(
    private injector: Injector,
    private searchResultsFilterablesService: SearchResultsFilterablesService,
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<Params> {
    if (route.params?.category) {
      return from(import('./search-results.query.generated')).pipe(
        switchMap(({ CategoryByNormalizedNameGQL }) =>
          this.injector.get(CategoryByNormalizedNameGQL).fetch({ name: route.params.category }),
        ),
        map(r => {
          const category = r.data.categoryByNormalizedName?.id;
          const others = parseArray(route.queryParams.searchCategories).filter(id => id !== category);
          return {
            searchCategories: category ? [category, ...others] : others,
            types: this.typesOf(route),
          };
        }),
      );
    }
    if (route.params?.location) {
      return from(
        Promise.all([
          import('utils/places-utility'),
          route.data?.isMunicipality ? import('utils/municipalities') : undefined,
        ]),
      ).pipe(
        map(([{ centerOfProvinces, centerOfRegions, places, regions }, municipalityData]) => {
          const location = route.params.location;
          const params: Params = { types: this.typesOf(route), banner: location };
          let center: { latitude?: number; longitude?: number } | undefined;
          if (route.data?.isRegion) {
            params.value = params.region = regions[location];
            center = centerOfRegions.find(c => c.value === params.value);
          }
          if (route.data?.isProvince) {
            params.value = params.province = places[location];
            center = centerOfProvinces.find(c => c.value === params.value);
          }
          if (route.data?.isMunicipality) {
            params.value = params.municipality = municipalityData.municipalities[location];
            params.distance = 50;
            center =
              municipalityData.centerOfMunicipalities.find(c => c.value === params.value) ??
              centerOfProvinces.find(c => c.value === params.value);
          }
          params.latitude = center?.latitude;
          params.longitude = center?.longitude;
          return params;
        }),
      );
    }
    return of({});
  }

  private typesOf(route: ActivatedRouteSnapshot): string[] {
    return (route.data.types || []).flatMap(
      (type: string) =>
        this.searchResultsFilterablesService[(type + 'FilterableIds') as Key<SearchResultsFilterablesService>],
    );
  }
}

export const searchEngineResolver: ResolveFn<Params> = route => inject(SearchEngineResolver).resolve(route);
